/**
 * App PostHog helpers — naming, safe capture, upload/doc helpers.
 * Never send filenames, amounts, OCR text, emails, or vendor names.
 */

import posthog from './posthog';

/** Safe, bounded fragment for putting identifiers in event names. */
export function toEventToken(value: string | undefined | null): string {
    const token = (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return token || 'unknown';
}

/** Map API/UI doc types to event-name prefixes (matches existing events). */
export function docTypeEventPrefix(docType: string | undefined | null): string {
    const t = (docType || '').toLowerCase();
    if (t === 'bank-statement' || t === 'bank_statement') return 'bank_statement';
    if (t === 'income' || t === 'revenue') return 'revenue';
    if (t === 'expense' || t === 'invoice') return 'expense';
    return toEventToken(docType);
}

export type FileSizeBucket =
    | 'lt_100kb'
    | '100kb_1mb'
    | '1_5mb'
    | '5_20mb'
    | 'gt_20mb';

export function fileSizeBucket(bytes: number | undefined | null): FileSizeBucket {
    const n = typeof bytes === 'number' && Number.isFinite(bytes) ? bytes : 0;
    if (n < 100 * 1024) return 'lt_100kb';
    if (n < 1024 * 1024) return '100kb_1mb';
    if (n < 5 * 1024 * 1024) return '1_5mb';
    if (n < 20 * 1024 * 1024) return '5_20mb';
    return 'gt_20mb';
}

export function fileExtension(filename: string | undefined | null): string {
    if (!filename || !filename.includes('.')) return 'unknown';
    const ext = filename.split('.').pop()?.toLowerCase() || 'unknown';
    return toEventToken(ext);
}

export type UploadErrorKind =
    | 'quota'
    | 'network'
    | 'http_4xx'
    | 'http_5xx'
    | 'http_other'
    | 'unknown';

export function uploadErrorKindFromStatus(status: number): UploadErrorKind {
    if (status === 402) return 'quota';
    if (status >= 400 && status < 500) return 'http_4xx';
    if (status >= 500) return 'http_5xx';
    if (status > 0) return 'http_other';
    return 'unknown';
}

export type AppEventProps = Record<string, string | number | boolean | null | undefined>;

/** Fire a named app event; never throws into product UI. */
export function captureAppEvent(event: string, properties?: AppEventProps): void {
    try {
        const cleaned: Record<string, string | number | boolean> = {};
        if (properties) {
            for (const [k, v] of Object.entries(properties)) {
                if (v === undefined || v === null) continue;
                cleaned[k] = v;
            }
        }
        posthog.capture(event, cleaned);
    } catch {
        /* analytics must not break the app */
    }
}

export type UploadSource =
    | 'reconciliation'
    | 'invoice_list'
    | 'revenue_list'
    | 'files'
    | 'bank_statements'
    | 'bulk_upload'
    | 'manual_entry';

export interface UploadTrackProps {
    docType: string;
    source: UploadSource | string;
    fileName?: string;
    fileSizeBytes?: number;
    errorKind?: UploadErrorKind | string;
}

function baseUploadProps(p: UploadTrackProps) {
    return {
        doc_type: p.docType,
        source: p.source,
        file_extension: fileExtension(p.fileName),
        file_size_bytes: typeof p.fileSizeBytes === 'number' ? p.fileSizeBytes : undefined,
        file_size_bucket: fileSizeBucket(p.fileSizeBytes),
    };
}

export function captureUploadAttempted(p: UploadTrackProps): void {
    const prefix = docTypeEventPrefix(p.docType);
    captureAppEvent(`${prefix}_upload_attempted`, baseUploadProps(p));
}

export function captureUploadSucceeded(p: UploadTrackProps): void {
    const prefix = docTypeEventPrefix(p.docType);
    captureAppEvent(`${prefix}_upload_succeeded`, baseUploadProps(p));
}

export function captureUploadFailed(p: UploadTrackProps): void {
    const prefix = docTypeEventPrefix(p.docType);
    captureAppEvent(`${prefix}_upload_failed`, {
        ...baseUploadProps(p),
        error_kind: p.errorKind || 'unknown',
    });
}

export function captureUploadQuotaExceeded(p: UploadTrackProps): void {
    const prefix = docTypeEventPrefix(p.docType);
    captureAppEvent(`${prefix}_upload_quota_exceeded`, {
        doc_type: p.docType,
        source: p.source,
        error_kind: 'quota',
    });
}

export function captureUploadBatchSummary(p: {
    docType: string;
    source: UploadSource | string;
    fileCount: number;
    successCount: number;
    failCount: number;
}): void {
    const prefix = docTypeEventPrefix(p.docType);
    captureAppEvent(`${prefix}_upload_batch_completed`, {
        doc_type: p.docType,
        source: p.source,
        file_count: p.fileCount,
        success_count: p.successCount,
        fail_count: p.failCount,
    });
}

/** Coarse app area from pathname for pageview context. */
export function appAreaFromPath(pathname: string): string {
    const seg = pathname.split('/').filter(Boolean)[0] || 'home';
    return toEventToken(seg);
}

export type BillingPlanTraits = {
    plan?: string | null;
    subscription_status?: string | null;
};

/**
 * Update PostHog super properties for plan (no event fired).
 * Pass known usage data, or omit to fetch getUsage() best-effort.
 */
export function refreshBillingPlanTraits(data?: BillingPlanTraits): void {
    const apply = (traits: BillingPlanTraits) => {
        try {
            posthog.register({
                plan: traits.plan || 'free',
                subscription_status: traits.subscription_status || 'free',
            });
        } catch {
            /* analytics must not break the app */
        }
    };

    if (data) {
        apply(data);
        return;
    }

    import('../services/billingApi')
        .then(({ billingApi }) =>
            billingApi.getUsage().then((res) => {
                if (res.success && res.data) apply(res.data);
            }).catch(() => {})
        )
        .catch(() => {});
}
